<template>
  <listing-layout>
    <h1 slot="heading" class="h2">Access API Tokens</h1>
    <div slot="content">
      <div class="alert alert-success" v-if="success">{{success}}</div>
      <div class="alert alert-warning" v-if="error">{{error}}</div>
      
      <form-token-create @created="loadRows" @error="error = $event" @success="success = $event" />

      <data-table :dataRows="rows" :dataColumns="columns" :options="options">
        <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="row.name" :row="row" />
        <div slot-scope="{ row }" slot="expandedRow">
          <ul class="list-unstyled mb-0 pt-2">
            <li class="pt-2">
              <strong>Status: </strong>
              <tableTextTemplate :row="row" :options="{ showActiveToken: true, inline: true }" />
            </li>
            <li class="pt-2">
              <strong>Last Active: </strong>
              <tableTextTemplate :text="`${row.last_used_at? row.last_used_at : '-'}`" :row="row" :options="{ inline: true }"/>
            </li>
            <li class="pt-2">
             <div class="d-flex">
                <button
                  class="btn btn-sm btn-primary mr-2"
                  @click="toggleActive(row)"
                >
                  {{ row.is_active ? 'Deactivate' : 'Activate' }}
                </button>

                <button
                  class="btn btn-sm btn-primary"
                  @click="confirmDelete(row)"
                >
                  Delete
                </button>
              </div>
            </li>
          </ul>
        </div>
      </data-table>
      <modal v-if="showDeleteModal" @close.prevent="showDeleteModal = false">
        <h3 slot="header" class="modal-title">Delete Confirmation</h3>
        <div slot="body" class="text-center">
          <p>Are you sure you want to delete this token?</p>
        </div>
        <div slot="footer">
          <button class="btn btn-outline-primary" @click.prevent="showDeleteModal = false">Cancel</button>
          <button class="btn btn-primary" @click="deleteConfirmed">Delete</button>
        </div>
      </modal>
    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
* to their respective fields in the ListingLayout component. */

import axios from 'axios'
import listingLayout from '@/components/ListingLayout'
import dataTable from '@/components/DataTable'
import modal from '@/components/Modal'
import tableTextTemplate from '@/components/DataTableFields/Text'
import tableButtonTemplate from '@/components/DataTableFields/Button'
import formTokenCreate from '../Add/TokenAdd'

export default {
  name: 'ApiTokens',
  props: [],
  components: {
    'listing-layout': listingLayout,
    'data-table': dataTable,
    tableTextTemplate,
    tableButtonTemplate,
    'form-token-create': formTokenCreate,
    modal
  },
  data() {
    return {
      status: '',
      error: '',
      success: '',
      rows: [],
      showDeleteModal: false,
      rowToDelete: null,
      columns: [
        {
          label: 'Name',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: false,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.name" :row="row" />`
          }),
          representedAs(row) { return `${row.name}` }
        },
        {
          label: 'Status',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :row="row" :options="{ showActiveToken: true, inline: true }"/>`
          })
        },
        {
          label: 'Last Active',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.last_used_at?row.last_used_at: '-'" :row="row" :options="{ inline: true }" />`
          })
        },
        {
          label: 'Actions',
          field: '',
          align: 'left',
          sortable: false,
          filterable: false,
          headerClass: 'd-none d-lg-table-cell column-lg-medium',
          visible: true,
          component: ({
            props: ['row'],
            template: `
              <div class="d-flex">
                <button class="btn btn-sm btn-primary mr-2" @click="toggleActive(row)">
                  {{ row.is_active ? 'Deactivate' : 'Activate' }}
                </button>
                <button class="btn btn-sm btn-primary" @click="confirmDelete(row)">
                  Delete
                </button>
              </div>
            `,
            methods: {
              toggleActive: this.toggleActive,
              confirmDelete: this.confirmDelete
            }
          })
        }
      ],
      options: {
        loaded: false
      }
    }
  },
  mounted() {
    this.loadRows()
  },
  methods: {
    loadRows() {
      axios.get(`${this.$api}/admin/api-tokens`).then((response) => {
        if (response.data.status === 'success') {
          this.rows = response.data.data.api_tokens
          this.options.loaded = true
        } else {
          this.error = 'Unable to receive data from the server. Please reload the page.'
        }
      })
    },
    toggleActive(row) {
      const action = row.is_active ? 'deactivate' : 'activate'
      axios.patch(`${this.$api}/admin/api-tokens/${row.id}/${action}`)
        .then((response) => {
          if (response.data.status === 'success') {
            this.success = `Token ${row.is_active ? 'deactivated' : 'activated'}!`
            this.loadRows()
          } else {
            this.error = (response.data.error) ? response.data.error : 'Unable to receive data from the server. Please reload the page.'
          }
        })
        .catch((err) => {
          // eslint-disable-next-line
          console.log(err)
        })
    },
    confirmDelete(row) {
      this.rowToDelete = row
      this.showDeleteModal = true
    },
    deleteConfirmed() {
      if (!this.rowToDelete) return
      axios.delete(`${this.$api}/admin/api-tokens/${this.rowToDelete.id}`)
        .then((response) => {
          if (response.data.status === 'success') {
            this.success = `Token deleted successfully!`
            this.loadRows()
          } else {
            this.error = (response.data.error) ? response.data.error : 'Unable to receive data from the server. Please reload the page.'
          }
          this.showDeleteModal = false
          this.rowToDelete = null
        })
        .catch((err) => {
          // eslint-disable-next-line
          console.log(err)
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ApiTokens/Listing/ApiTokens.vue